import React from 'react';
import { Warning, Lightbulb } from '@phosphor-icons/react';
import { Drawer, DrawerTrigger, DrawerContent, Button, Text, Flex, TagBadge } from '@chatsdet/ui';
import type { ImportStreamState, ImportRowState } from '../../hooks/useImportStream';

interface ImportErrorsDrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Called when drawer open state changes */
  onOpenChange: (isOpen: boolean) => void;
  /** Import stream state containing row errors */
  importState: ImportStreamState;
}

const formatErrorReason = (reason: string): string => {
  const reasonMap: Record<string, string> = {
    'unrecognized-format': 'Unrecognized Format',
    'missing-required-fields': 'Missing Fields',
    'ambiguous-data': 'Ambiguous Data',
    'insufficient-context': 'Insufficient Context',
    'data-quality-issue': 'Data Quality Issue',
  };
  return reasonMap[reason] ?? reason;
};

function ImportErrorCard({ row }: { row: ImportRowState }) {
  const errorJson = row.errorJson;
  const hasStructuredError = !!errorJson;

  return (
    <div className="border border-border rounded-lg p-4 bg-card">
      <Flex align="center" gap="sm" className="mb-2">
        <TagBadge name={<>Row {row.rowIndex}</>} color="gray" hideDot className="text-xs" />
        {hasStructuredError ? (
          <TagBadge name={formatErrorReason(errorJson.reason)} color="red" hideDot className="text-xs" />
        ) : (
          <TagBadge name="Error" color="red" hideDot className="text-xs" />
        )}
      </Flex>

      <Flex align="start" gap="sm" className="mb-2">
        <Warning size={16} className="text-destructive mt-0.5 shrink-0" />
        <Text size="sm" className="text-foreground">
          {hasStructuredError ? errorJson.details : (row.errorMessage ?? 'Unknown error')}
        </Text>
      </Flex>

      {hasStructuredError && errorJson.suggestions && errorJson.suggestions.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border">
          <Flex align="center" gap="xs" className="mb-2">
            <Lightbulb size={14} className="text-muted-foreground" />
            <Text
              size="xs"
              weight="medium"
              className="text-muted-foreground uppercase tracking-wide"
            >
              Suggestions
            </Text>
          </Flex>
          <ul className="list-disc list-inside space-y-1">
            {errorJson.suggestions.map((suggestion, i) => (
              <li key={i} className="text-sm text-muted-foreground">
                {suggestion}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Drawer that displays import errors with structured details and suggestions.
 */
export const ImportErrorsDrawer: React.FC<ImportErrorsDrawerProps> = ({
  isOpen,
  onOpenChange,
  importState,
}) => {
  const failedRows = React.useMemo(() => {
    return Object.values(importState.rows)
      .filter((r) => r.status === 'failed')
      .sort((a, b) => a.rowIndex - b.rowIndex);
  }, [importState.rows]);

  return (
    <DrawerTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      {/* Hidden trigger button - drawer is controlled externally */}
      <Button className="hidden" />

      <Drawer title="Import Errors">
        <DrawerContent className="p-0 flex flex-col h-full">
          {/* Header with count */}
          <div className="px-4 py-3 border-b border-border bg-muted/30">
            <Flex align="center" gap="sm">
              <Warning size={16} className="text-destructive" />
              <Text size="sm" weight="medium">
                {failedRows.length} row{failedRows.length === 1 ? '' : 's'} failed to import
              </Text>
            </Flex>
            <Text size="xs" className="text-muted-foreground mt-1">
              Review the errors below. You may need to update your CSV and re-upload.
            </Text>
          </div>

          {/* Error list */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {failedRows.length === 0 ? (
              <div className="text-center py-8">
                <Text size="sm" className="text-muted-foreground">
                  No errors to display
                </Text>
              </div>
            ) : (
              failedRows.map((row) => <ImportErrorCard key={row.rowId} row={row} />)
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-border bg-muted/20 shrink-0">
            <Flex justify="between" align="center">
              <Text size="xs" className="text-muted-foreground">
                {importState.completedRows} workflow{importState.completedRows === 1 ? '' : 's'}{' '}
                created successfully
              </Text>
              <Button variant="outline" size="sm" onPress={() => onOpenChange(false)}>
                Close
              </Button>
            </Flex>
          </div>
        </DrawerContent>
      </Drawer>
    </DrawerTrigger>
  );
};
